import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { IntlProvider } from 'react-intl'
import '@formatjs/intl-getcanonicallocales/polyfill'

import useShipmentTrackingData from './useShipmentTrackingData';
import pushStat, { configureStats } from './pushStat';
import productionPreviewMockData from './productionPreviewData'; // DO NOT CHANGE THIS IMPORT. USED BY PREVIEW FROM SHIP-IT
import { useLocaleMessages, DEFAULT_LOCALE } from "./locales";

import ErrorBoundary, { InnerErrorBoundary, LambdaError } from './ErrorBoundary';
import TrackingPage from './TrackingPage';
import DevControls from './DevControls';


export default function App({ testData }) {
  return <ErrorBoundary>
    <InnerApp testData={testData} />
  </ErrorBoundary>;
}


function getLocale(shipment) {
  if( !shipment ) {
    return null;
  }

  if( shipment.locale ) {
    try {
      return Intl.getCanonicalLocales(shipment.locale)[0];
    } catch(_) {
      return DEFAULT_LOCALE;
    }
  }
  else {
    return DEFAULT_LOCALE;
  }
}


function InnerApp({ testData }) {
  const url = new URL(window.location.href);
  const isPreviewMode = url.searchParams.get('preview');
  const mockPreviewData = testData
    || (isPreviewMode && productionPreviewMockData);

  const trackingArgs = {
    isPreview: !!mockPreviewData,
    mockPreviewData,
  };

  const idV1 = url.searchParams.get('d');
  if (idV1) {
    trackingArgs['id'] = idV1;
  }
  else {
    // if this is a query-string request,  either the shipment_id will be present, or the shipment properties
    ['branding_id', 'carrier_code', 'tracking_number', 'order_number', 'postal_code', 'shipment_id']
        .forEach(q => trackingArgs[q] = url.searchParams.get(q));
  }

  const [data, error, setData] = useShipmentTrackingData(trackingArgs);

  if (error) {
    const message = error?.response?.data?.error_message;
    const carrierTrackingUrl = error?.response?.data?.carrier_tracking_url;

    throw new LambdaError(
      message || `LambdaError(${carrierTrackingUrl ? 'message' : 'message & carrierTrackingUrl'}) MISSING`,
      carrierTrackingUrl
    );
  }

  if (url.searchParams.get('mockShipment')) {
    data.shipment = productionPreviewMockData.shipment;
    data.tracking = productionPreviewMockData.tracking;
  }

  useEffect(() => {
    if( data.branding && data.shipment && !mockPreviewData ) {
      configureStats({
        isPreview: !!mockPreviewData,
        customer_id: data.shipment.external_account_id || (data.tracking && data.tracking.customer_id),
        branding_id: data.branding.branding_id,
        tracking_number: data.shipment.tracking_number,
        carrier_code: data.shipment.carrier_code,
        buyer_email: data.shipment.destination.email,
      });
      pushStat('view');
    }
  }, [data, data.tracking, data.branding, data.shipment, mockPreviewData]);

  const locale = getLocale(data.shipment);
  const messages = useLocaleMessages( locale );

  if( !locale || !messages ) {
    return null;
  }

  return <InnerErrorBoundary carrierTrackingUrl={data.carrier_tracking_url}>
    <IntlProvider messages={messages} locale={locale} defaultLocale={DEFAULT_LOCALE}>
      <TrackingPage data={data} />
      <DevControls { ...{ data, setData } } />
    </IntlProvider>
  </InnerErrorBoundary>;
}
App.propTypes = {
  testData: PropTypes.object
};
